import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import styles from "./EventCarouse.module.css";

const EventCarouse = () => {
  const router = useRouter();
  const [announcements, setAnnouncements] = useState([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    const fetchAnnouncements = async () => {
      try {
        const endpoint = `${process.env.NEXT_PUBLIC_API_URL}/announcements`;
        const res = await fetch(endpoint, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (res.ok && data.announcements) {
          setAnnouncements(data.announcements);
        } else {
          console.error("Failed to load announcements:", data.message || data);
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
      }
    };

    fetchAnnouncements();
  }, []);

  const handleNavigation = (eventId) => {
    router.push(`/event-details/${eventId}`);
  };

  const handleBookNow = (e, eventId) => {
    e.preventDefault();
    e.stopPropagation();
    handleNavigation(eventId);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (!mounted) return null;

  return (
    <>
      <Swiper
        className="swiper event-carousel rbt-arrow-between rbt-dot-bottom-center pb--20 icon-bg-primary"
        slidesPerView={1}
        spaceBetween={30}
        modules={[Navigation, Pagination]}
        pagination={{
          el: ".rbt-swiper-pagination",
          clickable: true,
        }}
        navigation={{
          nextEl: ".rbt-arrow-right",
          prevEl: ".rbt-arrow-left",
        }}
        breakpoints={{
          481: { slidesPerView: 1 },
          768: { slidesPerView: 2 },
          992: { slidesPerView: 3 },
        }}
      >
        {announcements.map((data, index) => {
          const dateObj = data.fromDate ? new Date(data.fromDate) : null;
          const badgeLabel = data.badgeDate
            ? data.badgeDate
            : dateObj
              ? dateObj.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
              })
              : "";
          const badgeYear = data.badgeYear
            ? data.badgeYear
            : dateObj
              ? dateObj.getFullYear()
              : "";
          const titleText =
            typeof data.title === "string"
              ? data.title
              : data.title?.title || "";
          const timeText = data.timeFrom
            ? data.timeFrom
            : data.timeFrom && data.timeTo
              ? `${data.timeFrom} - ${data.timeTo}`
              : "";
          const imageUrl = data.thumbnail
            ? data.thumbnail
            : "/images/others/announcement.jpg";

          const slotsAvailable = data.maxStudents;
          const isLow = slotsAvailable <= 3;

          return (
            <SwiperSlide
              style={{ marginBottom: "30px" }}
              key={data._id || index}
            >
              <div className="single-slide">
                <div
                  className={`rbt-card event-grid-card variation-01 rbt-hover ${isLow ? `${styles.lowSlotsHover}` : ""
                    }`}
                  style={{
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={() => handleNavigation(data._id)}
                >
                  <div
                    className="rbt-card-img position-relative overflow-hidden"
                    style={{ height: 250, flexShrink: 0 }}
                  >
                    <Image
                      src={imageUrl}
                      fill
                      style={{ objectFit: "cover" }}
                      alt="Announcement Image"
                    />
                    <div
                      className="rbt-badge-3 bg-white position-absolute top-0 end-0 m-3"
                      style={{ zIndex: 10 }}
                    >
                      <span className="d-block">{badgeLabel}</span>
                      <span className="d-block">{badgeYear}</span>
                    </div>
                  </div>
                  <div
                    className="rbt-card-body d-flex flex-column justify-content-between p-3"
                    style={{ flex: 1 }}
                  >
                    <ul className="rbt-meta mb-3" style={{ minHeight: 20 }}>
                      <li>
                        <i className="feather-map-pin"></i>{" "}
                        {truncateText(data.venue, 20)}
                      </li>
                      <li>
                        <i className="feather-clock"></i> {timeText}
                      </li>
                    </ul>
                    <h4
                      className="rbt-card-title"
                      style={{
                        minHeight: 60,
                        marginBottom: 20,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {truncateText(titleText, 80)}
                    </h4>
                    {data.maxStudents && (
                      <div
                        className={`${styles.availableSlots} ${isLow ? `${styles.lowSlotsCard}` : ""
                          }`}
                      >
                        <div
                          className={`${styles.slotIcon} ${isLow ? styles.lowSlotIcon : ""
                            }`}
                        >
                          <i className="feather-users"></i>
                        </div>
                        <div className={styles.slotText}>
                          <span className={styles.label}>Slots Available</span>
                          <span
                            className={`${styles.count} ${isLow ? styles.lowSlots : ""
                              }`}
                          >
                            {data.maxStudents}
                          </span>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      className="rbt-btn btn-border btn-sm radius-round align-self-start"
                      onClick={(e) => handleBookNow(e, data._id)}
                      style={{ marginTop: "auto" }}
                    >
                      View details <i className="feather-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}

        <div className="rbt-swiper-arrow rbt-arrow-left">
          <div className="custom-overfolow">
            <i className="rbt-icon feather-arrow-left"></i>
            <i className="rbt-icon-top feather-arrow-left"></i>
          </div>
        </div>

        <div className="rbt-swiper-arrow rbt-arrow-right">
          <div className="custom-overfolow">
            <i className="rbt-icon feather-arrow-right"></i>
            <i className="rbt-icon-top feather-arrow-right"></i>
          </div>
        </div>

        <div className="rbt-swiper-pagination" style={{ bottom: "0" }}></div>
      </Swiper>

      {announcements.length > 3 && (
        <div className="row">
          <div className="col-lg-12">
            <div className="load-more-btn mt--30 text-center">
              <Link
                href="/announcements"
                className="rbt-btn btn-gradient btn-lg hover-icon-reverse"
              >
                <span className="icon-reverse-wrapper">
                  <span className="btn-text">Load More Events</span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                  <span className="btn-icon">
                    <i className="feather-arrow-right"></i>
                  </span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventCarouse;
